import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  getStoredTokens,
  initiateOAuth,
  OAUTH_PENDING_STORAGE_KEY,
} from "@/lib/oauth/mcp-oauth";
import type { OAuthTrace } from "@/lib/oauth/oauth-trace";
import {
  buildOAuthRequest,
  type BuiltOAuthRequest,
} from "@/lib/oauth/oauth-request";
import { normalizeRegistrationMode } from "@/shared/xaa.js";
import type { HostedOAuthRequiredDetails } from "@/lib/hosted-oauth-required";
import {
  HOSTED_OAUTH_PENDING_STORAGE_KEY,
  clearHostedOAuthPendingState,
  matchesHostedOAuthServerIdentity,
  writeHostedOAuthPendingMarker,
} from "@/lib/hosted-oauth-callback";
import {
  clearHostedOAuthResumeMarker,
  type HostedOAuthState,
  type HostedOAuthStatus,
  type HostedOAuthSurface,
  isHostedOAuthBusy,
  readHostedOAuthResumeMarker,
  sanitizeHostedOAuthErrorMessage,
} from "@/lib/hosted-oauth-resume";
import {
  validateHostedServer,
  type HostedServerValidateContext,
} from "@/lib/apis/web/servers-api";
import { describeHostedOAuthFailure } from "@/lib/hosted-oauth-failure";
import { toast } from "@/lib/toast";
import { slugify } from "@/lib/scenario-session";
import { captureCurrentReturnPath, routePaths } from "@/lib/app-navigation";
import { ingestOAuthTraceLogs } from "@/stores/traffic-log-store";
import {
  isServerFormOAuthProtocolMode,
  resolveOAuthProtocolSelection,
} from "@/shared/types.js";

const INLINE_TOKEN_POLL_ATTEMPTS = 15;
const RESUME_TOKEN_POLL_ATTEMPTS = 24;
const TOKEN_POLL_MS = 250;
const VALIDATION_RETRY_ATTEMPTS = 3;
const VALIDATION_RETRY_MS = 400;

const TOKEN_MISSING_ERROR =
  "Authorization completed, but MCPJam could not find the access token. Try again.";
const VALIDATION_ERROR =
  "Authorization completed, but MCPJam could not verify access. Try again.";
const RUNTIME_OAUTH_ERROR =
  "Authorization expired or is missing. Authorize again to continue.";

export interface HostedOAuthServerDescriptor {
  serverId: string;
  serverName: string;
  useOAuth: boolean;
  serverUrl: string | null;
  clientId: string | null;
  oauthScopes: string[] | null;
  oauthProtocolMode?: string | null;
  oauthProtocolVersion?: string | null;
  wireProtocolVersion?: string | null;
  /**
   * Per-server OAuth facts the shared request builder needs.
   *
   * These used to have no home on this descriptor, so the hosted authorization
   * simply omitted them and produced different wire behavior than a local
   * connect against the same server. They are optional because a hosted
   * bootstrap payload that does not carry them yet must keep working — but the
   * hosted path now THREADS whatever it is given instead of dropping it.
   */
  oauthResourceUrl?: string | null;
  hasClientSecret?: boolean | null;
  oauthCustomHeaders?: Record<string, string> | null;
  oauthAllowPathScopedIssuer?: boolean | null;
  registrationMode?: string | null;
  /** When true, server was opted in after session start (copy / UX hints). */
  optional?: boolean;
  /**
   * Whether this server must be authorized BEFORE the session can be used.
   *
   * `useOAuth` cannot answer that: it is the derived compat mirror of the
   * canonical `authMethod`, and an `auto` (discover) row carries `useOAuth:
   * true` while its whole contract is to connect unauthenticated and escalate
   * only on a real 401. Gating on the mirror is what asked recipients of a
   * shared scenario to authorize servers that have no authorization server at
   * all, behind a prompt that could never succeed.
   *
   * `false` starts the server satisfied — it never reaches the auth panel or
   * blocks the composer — while KEEPING it in the authorizable set, so a
   * genuine 401 at runtime still routes through {@link markOAuthRequired} and
   * gets its Authorize action. `undefined` keeps the legacy behavior (gate
   * every `useOAuth` server up front) for callers with no better answer.
   */
  authorizationRequiredUpfront?: boolean;
}

export function resolveHostedOAuthProtocolSelection(
  server: Pick<
    HostedOAuthServerDescriptor,
    "oauthProtocolMode" | "oauthProtocolVersion" | "wireProtocolVersion"
  >
) {
  return resolveOAuthProtocolSelection({
    mode: isServerFormOAuthProtocolMode(server.oauthProtocolMode)
      ? server.oauthProtocolMode
      : undefined,
    legacyProtocolVersion: server.oauthProtocolVersion ?? undefined,
    wireProtocolVersion: server.wireProtocolVersion ?? undefined,
  });
}

function buildHostedOAuthStateMap(
  oauthServers: HostedOAuthServerDescriptor[],
  surface: HostedOAuthSurface,
  isVaultBacked: boolean,
  verifyVaultCredentialOnLoad: boolean,
  /**
   * Servers the RUNTIME has since proven need authorization (a tagged 401 via
   * {@link markOAuthRequired}). Rebuilds must not re-satisfy those, or a
   * descriptor identity change would silently retract a prompt the user needs.
   */
  runtimeRequiredServerIds: ReadonlySet<string>,
  previous: Record<string, HostedOAuthState> = {}
): Record<string, HostedOAuthState> {
  const resumeMarker = readHostedOAuthResumeMarker(surface);
  const nextState: Record<string, HostedOAuthState> = {};

  for (const server of oauthServers) {
    const existing = previous[server.serverId];
    const hasToken = isVaultBacked
      ? false
      : !!getStoredTokens(server.serverName, server.serverUrl ?? undefined)
          ?.access_token;
    const matchesResume =
      resumeMarker != null &&
      matchesHostedOAuthServerIdentity(
        {
          serverName: resumeMarker.serverName,
          serverUrl: resumeMarker.serverUrl,
        },
        {
          serverName: server.serverName,
          serverUrl: server.serverUrl,
        }
      );
    const serverUrl = server.serverUrl ?? existing?.serverUrl ?? null;

    let status: HostedOAuthStatus;
    let errorMessage: string | null = existing?.errorMessage ?? null;

    if (existing?.status === "launching") {
      status = "launching";
      errorMessage = null;
    } else if (matchesResume && resumeMarker?.errorMessage) {
      status = "error";
      errorMessage = resumeMarker.errorMessage;
    } else if (matchesResume) {
      status = hasToken || isVaultBacked ? "verifying" : "resuming";
      errorMessage = null;
    } else if (runtimeRequiredServerIds.has(server.serverId)) {
      // A tagged 401 proved on the wire that this server needs authorizing, so
      // no rebuild may retract the prompt — not a stale stored token (which
      // would downgrade it to "verifying"), and not an
      // `authorizationRequiredUpfront: false` descriptor. Only consent already
      // given outranks it: the launching/resume branches above, or an
      // authorization that has since completed as ready/verifying.
      status =
        existing?.status === "ready" || existing?.status === "verifying"
          ? existing.status
          : "needs_auth";
      if (status !== "needs_auth") {
        errorMessage = null;
      }
    } else if (server.authorizationRequiredUpfront === false) {
      // Satisfied by construction: this row can use OAuth but does not demand
      // it before the session runs, so there is no credential to wait for and
      // nothing to verify — a leftover token or vault record from an earlier
      // connect attempt must not resurrect a prompt the server never needed.
      // Runtime escalation (a real 401) still flips it to needs_auth, through
      // the runtime-required branch above.
      status = "ready";
      errorMessage = null;
    } else if (hasToken) {
      status = existing?.status === "ready" ? "ready" : "verifying";
      errorMessage = null;
    } else if (isVaultBacked && verifyVaultCredentialOnLoad) {
      status = existing?.status === "ready" ? "ready" : "verifying";
      errorMessage = null;
    } else if (existing?.status === "ready") {
      status = "ready";
      errorMessage = null;
    } else if (existing?.status === "error") {
      status = "error";
    } else {
      status = "needs_auth";
      errorMessage = null;
    }

    nextState[server.serverId] = {
      status,
      errorMessage,
      serverUrl,
    };
  }

  return nextState;
}

function setStoredOAuthTokenState(
  serverName: string,
  nextState: HostedOAuthState,
  setState: Dispatch<SetStateAction<Record<string, HostedOAuthState>>>,
  serverId: string
) {
  setState((previous) => ({
    ...previous,
    [serverId]: {
      ...nextState,
      serverUrl: nextState.serverUrl ?? previous[serverId]?.serverUrl ?? null,
    },
  }));

  if (nextState.status === "needs_auth" || nextState.status === "error") {
    localStorage.removeItem(`mcp-tokens-${serverName}`);
  }
}

async function waitForStoredAccessToken(
  serverName: string,
  attempts: number,
  serverUrl?: string
): Promise<string | null> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    const accessToken = getStoredTokens(serverName, serverUrl)?.access_token;
    if (typeof accessToken === "string" && accessToken.trim()) {
      return accessToken;
    }

    await new Promise((resolve) => window.setTimeout(resolve, TOKEN_POLL_MS));
  }

  return null;
}

async function validateWithRetry(
  serverId: string,
  oauthAccessToken?: string,
  hostedContext?: HostedServerValidateContext
): Promise<{ ok: true } | { ok: false; error: unknown }> {
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= VALIDATION_RETRY_ATTEMPTS; attempt++) {
    try {
      await validateHostedServer(
        serverId,
        oauthAccessToken,
        undefined,
        hostedContext
      );
      return { ok: true };
    } catch (error) {
      lastError = error;
      if (attempt < VALIDATION_RETRY_ATTEMPTS) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, VALIDATION_RETRY_MS)
        );
      }
    }
  }

  return { ok: false, error: lastError };
}

export interface UseHostedOAuthGateOptions {
  surface: HostedOAuthSurface;
  pendingKey: string;
  servers: HostedOAuthServerDescriptor[];
  projectId?: string | null;
  scenarioId?: string;
  isAuthenticated?: boolean;
}

export interface UseHostedOAuthGateResult {
  oauthStateByServerId: Record<string, HostedOAuthState>;
  pendingOAuthServers: Array<{
    server: HostedOAuthServerDescriptor;
    state: HostedOAuthState;
  }>;
  hasBusyOAuth: boolean;
  authorizeServer: (server: HostedOAuthServerDescriptor) => Promise<void>;
  markOAuthRequired: (details?: HostedOAuthRequiredDetails) => void;
}

export function useHostedOAuthGate({
  surface,
  pendingKey,
  servers,
  projectId,
  scenarioId,
  isAuthenticated = false,
}: UseHostedOAuthGateOptions): UseHostedOAuthGateResult {
  const oauthServers = useMemo(
    () => servers.filter((server) => server.useOAuth),
    [servers]
  );
  // Where does the token LAND? Vault-backed surfaces complete server-side, so
  // nothing is written to localStorage and polling for it would only burn the
  // resume window before reporting a token that was never missing. The score
  // surface completes through `completeHostedOAuthCallback` with a guest
  // bearer — server-side, exactly like a scenario guest — so it belongs here
  // even though it has neither a signed-in user nor a scenario.
  const isVaultBacked = isAuthenticated || !!scenarioId || surface === "score";
  const verifyVaultCredentialOnLoad = isAuthenticated;
  // Servers a runtime 401 has proven need authorization after all. Kept
  // separately from the status map because the map is REBUILT from the
  // descriptors on every identity change: without this, a server declared
  // "not required up front" would swallow its own runtime prompt on the next
  // rebuild. Ids only — the descriptor stays the source of everything else.
  const [runtimeRequiredServerIds, setRuntimeRequiredServerIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  // A runtime 401 that arrived before any server had joined the authorizable
  // set, held until one does. `undefined` means nothing is held; `null` means a
  // request that carried no server details (it applies to whatever arrives).
  const [deferredOAuthRequirement, setDeferredOAuthRequirement] = useState<
    HostedOAuthRequiredDetails | null | undefined
  >(undefined);
  const [oauthStateByServerId, setOAuthStateByServerId] = useState<
    Record<string, HostedOAuthState>
  >(() =>
    buildHostedOAuthStateMap(
      oauthServers,
      surface,
      isVaultBacked,
      verifyVaultCredentialOnLoad,
      new Set()
    )
  );
  const oauthStateByServerIdRef = useRef(oauthStateByServerId);
  const processingServerIdsRef = useRef<Set<string>>(new Set());
  // Bumped per server every time a runtime 401 escalates it. Credential
  // verification reads it before awaiting and again after: a 401 observed on the
  // wire is NEWER evidence than a verification that started before it, so a
  // success arriving late must not overwrite the prompt that 401 raised.
  const runtimeRequiredEpochRef = useRef<Map<string, number>>(new Map());
  const isUnmountedRef = useRef(false);

  useEffect(() => {
    oauthStateByServerIdRef.current = oauthStateByServerId;
  }, [oauthStateByServerId]);

  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    setOAuthStateByServerId((previous) =>
      buildHostedOAuthStateMap(
        oauthServers,
        surface,
        isVaultBacked,
        verifyVaultCredentialOnLoad,
        runtimeRequiredServerIds,
        previous
      )
    );
  }, [
    oauthServers,
    surface,
    isVaultBacked,
    verifyVaultCredentialOnLoad,
    runtimeRequiredServerIds,
  ]);

  // `authorizeServer` is declared below and changes identity with its deps.
  // Reaching it through a ref keeps the Reconnect action current without
  // adding a dependency that would re-run the processing effect.
  const authorizeServerRef = useRef<
    ((server: HostedOAuthServerDescriptor) => Promise<void>) | null
  >(null);

  const showHostedOAuthFailureToast = useCallback(
    (error: unknown, server: HostedOAuthServerDescriptor) => {
      const copy = describeHostedOAuthFailure(error, server.serverName);
      if (!copy) {
        return;
      }

      toast.error(copy.title, {
        id: `hosted-oauth-failure-${server.serverId}`,
        description: copy.detail.join("\n"),
        descriptionClassName: "whitespace-pre-wrap break-all font-mono text-xs",
        ...(copy.action === "reconnect"
          ? {
              action: {
                label: "Reconnect",
                onClick: () => {
                  void authorizeServerRef.current?.(server);
                },
              },
            }
          : {}),
        ...(copy.action === "retry"
          ? {
              action: {
                label: "Retry",
                onClick: () => {
                  setOAuthStateByServerId((previous) => ({
                    ...previous,
                    [server.serverId]: {
                      status: "verifying",
                      errorMessage: null,
                      serverUrl:
                        previous[server.serverId]?.serverUrl ??
                        server.serverUrl ??
                        null,
                    },
                  }));
                },
              },
            }
          : {}),
      });
    },
    []
  );

  useEffect(() => {
    if (oauthServers.length === 0) {
      return;
    }

    const processServer = async (
      server: HostedOAuthServerDescriptor,
      status: HostedOAuthStatus
    ) => {
      if (processingServerIdsRef.current.has(server.serverId)) {
        return;
      }

      processingServerIdsRef.current.add(server.serverId);
      try {
        const isResume = status === "resuming";
        const accessToken = isVaultBacked
          ? null
          : isResume
          ? await waitForStoredAccessToken(
              server.serverName,
              RESUME_TOKEN_POLL_ATTEMPTS,
              server.serverUrl ?? undefined
            )
          : getStoredTokens(server.serverName, server.serverUrl ?? undefined)
              ?.access_token ?? null;

        if (isUnmountedRef.current) return;

        if (!accessToken && !isVaultBacked) {
          clearHostedOAuthResumeMarker();
          setStoredOAuthTokenState(
            server.serverName,
            {
              status: "error",
              errorMessage: TOKEN_MISSING_ERROR,
              serverUrl:
                oauthStateByServerIdRef.current[server.serverId]?.serverUrl ??
                server.serverUrl,
            },
            setOAuthStateByServerId,
            server.serverId
          );
          return;
        }

        if (status !== "verifying") {
          setOAuthStateByServerId((previous) => ({
            ...previous,
            [server.serverId]: {
              status: "verifying",
              errorMessage: null,
              serverUrl:
                previous[server.serverId]?.serverUrl ??
                server.serverUrl ??
                null,
            },
          }));
        }

        const epochBeforeValidation =
          runtimeRequiredEpochRef.current.get(server.serverId) ?? 0;

        const validation = await validateWithRetry(
          server.serverId,
          accessToken ?? undefined,
          scenarioId && projectId
            ? {
                projectId,
                serverId: server.serverId,
                serverName: server.serverName,
                accessScope: "chat_v2",
                scenarioId,
              }
            : undefined
        );
        if (isUnmountedRef.current) return;

        // A 401 landed while this verification was in flight. Its prompt is the
        // newer truth about the credential; leave it standing.
        if (
          (runtimeRequiredEpochRef.current.get(server.serverId) ?? 0) !==
          epochBeforeValidation
        ) {
          return;
        }

        if (validation.ok) {
          clearHostedOAuthResumeMarker();
          setOAuthStateByServerId((previous) => ({
            ...previous,
            [server.serverId]: {
              status: "ready",
              errorMessage: null,
              serverUrl:
                previous[server.serverId]?.serverUrl ??
                server.serverUrl ??
                null,
            },
          }));
          return;
        }

        console.error("[useHostedOAuthGate] OAuth validation failed", {
          surface,
          serverId: server.serverId,
          serverName: server.serverName,
          error: validation.error,
        });
        // The inline banner carries the generic copy. A refresh that failed
        // upstream gets a toast on top of it, because the two upstream causes
        // need opposite actions from the user and the banner cannot say which.
        showHostedOAuthFailureToast(validation.error, server);
        clearHostedOAuthResumeMarker();
        setStoredOAuthTokenState(
          server.serverName,
          {
            status: "error",
            errorMessage: sanitizeHostedOAuthErrorMessage(
              validation.error,
              VALIDATION_ERROR
            ),
            serverUrl:
              oauthStateByServerIdRef.current[server.serverId]?.serverUrl ??
              server.serverUrl,
          },
          setOAuthStateByServerId,
          server.serverId
        );
      } finally {
        processingServerIdsRef.current.delete(server.serverId);
      }
    };

    for (const server of oauthServers) {
      const currentStatus = oauthStateByServerId[server.serverId]?.status;
      if (currentStatus === "resuming" || currentStatus === "verifying") {
        void processServer(server, currentStatus);
      }
    }
  }, [
    oauthServers,
    oauthStateByServerId,
    surface,
    isVaultBacked,
    scenarioId,
    projectId,
    showHostedOAuthFailureToast,
  ]);

  const authorizeServer = useCallback(
    async (server: HostedOAuthServerDescriptor) => {
      clearHostedOAuthResumeMarker();
      clearHostedOAuthPendingState();
      setOAuthStateByServerId((previous) => ({
        ...previous,
        [server.serverId]: {
          status: "launching",
          errorMessage: null,
          serverUrl:
            previous[server.serverId]?.serverUrl ?? server.serverUrl ?? null,
        },
      }));

      if (!server.serverUrl) {
        setOAuthStateByServerId((previous) => ({
          ...previous,
          [server.serverId]: {
            status: "error",
            errorMessage:
              "This server is missing its OAuth URL. Try again or contact the owner.",
            serverUrl: previous[server.serverId]?.serverUrl ?? null,
          },
        }));
        return;
      }

      const captured = captureCurrentReturnPath();
      const returnPath =
        captured && captured !== routePaths.servers
          ? captured
          : `/${slugify(server.serverName)}`;
      writeHostedOAuthPendingMarker({
        surface,
        projectId,
        serverId: server.serverId,
        serverName: server.serverName,
        serverUrl: server.serverUrl,
        accessScope: scenarioId
          ? "chat_v2"
          : isAuthenticated
          ? "project_member"
          : undefined,
        scenarioId,
        returnPath,
      });
      // The sentinel is a legacy boolean; the structured marker written just
      // above is the real state. A caller that names the marker's own key would
      // overwrite that JSON with `"true"`, and the marker reader — which
      // requires an object — would clear it and strand the callback. Refuse the
      // write rather than destroy the marker: the sentinel is redundant, the
      // marker is not.
      if (pendingKey === HOSTED_OAUTH_PENDING_STORAGE_KEY) {
        console.error(
          "useHostedOAuthGate: pendingKey must not be the hosted marker key; ignoring the sentinel write."
        );
      } else {
        localStorage.setItem(pendingKey, "true");
      }
      localStorage.setItem("mcp-oauth-return-hash", returnPath);

      const protocolSelection =
        resolveHostedOAuthProtocolSelection(server);

      // Same builder as connect and reconnect. This call site used to omit
      // allowPathScopedIssuer, hasClientSecret, customHeaders, resourceUrl, and
      // registrationMode entirely, so a hosted connect produced different wire
      // behavior than a local one against the same server.
      //
      // The builder refuses a configured resource indicator that is not this
      // server's, before the redirect. Report that like any other
      // could-not-start failure rather than letting it escape the callback.
      let request: BuiltOAuthRequest;
      try {
        request = buildOAuthRequest(
          {
            serverName: server.serverName,
            serverUrl: server.serverUrl,
            clientId: server.clientId ?? undefined,
            scopes: server.oauthScopes ?? undefined,
            resourceUrl: server.oauthResourceUrl ?? undefined,
            hasClientSecret: server.hasClientSecret === true,
            customHeaders: server.oauthCustomHeaders ?? undefined,
            allowPathScopedIssuer: server.oauthAllowPathScopedIssuer === true,
            registrationMode: normalizeRegistrationMode(
              server.registrationMode ?? undefined,
            ),
            protocolMode: protocolSelection.mode,
            protocolVersion: protocolSelection.protocolVersion,
            protocolResolutionSource: protocolSelection.source,
            onTraceUpdate: (oauthTrace: OAuthTrace) => {
              ingestOAuthTraceLogs({
                serverId: server.serverId,
                serverName: server.serverName,
                trace: oauthTrace,
              });
            },
          },
          { intent: "hosted-connect" },
        );
      } catch (error) {
        clearHostedOAuthPendingState();
        localStorage.removeItem(OAUTH_PENDING_STORAGE_KEY);
        localStorage.removeItem("mcp-oauth-return-hash");
        localStorage.removeItem(pendingKey);
        setOAuthStateByServerId((previous) => ({
          ...previous,
          [server.serverId]: {
            status: "error",
            errorMessage: sanitizeHostedOAuthErrorMessage(
              error instanceof Error ? error.message : undefined,
              "Authorization could not be started. Try again."
            ),
            serverUrl:
              previous[server.serverId]?.serverUrl ?? server.serverUrl ?? null,
          },
        }));
        return;
      }

      const result = await initiateOAuth(request);

      if (!result.success) {
        clearHostedOAuthPendingState();
        localStorage.removeItem(OAUTH_PENDING_STORAGE_KEY);
        localStorage.removeItem("mcp-oauth-return-hash");
        localStorage.removeItem(pendingKey);
        setOAuthStateByServerId((previous) => ({
          ...previous,
          [server.serverId]: {
            status: "error",
            errorMessage: sanitizeHostedOAuthErrorMessage(
              result.error,
              "Authorization could not be started. Try again."
            ),
            serverUrl:
              previous[server.serverId]?.serverUrl ?? server.serverUrl ?? null,
          },
        }));
        return;
      }

      const accessToken = isVaultBacked
        ? null
        : await waitForStoredAccessToken(
            server.serverName,
            INLINE_TOKEN_POLL_ATTEMPTS,
            server.serverUrl ?? undefined
          );

      if (accessToken) {
        clearHostedOAuthPendingState();
        localStorage.removeItem(OAUTH_PENDING_STORAGE_KEY);
        localStorage.removeItem("mcp-oauth-return-hash");
        localStorage.removeItem(pendingKey);
      }

      setOAuthStateByServerId((previous) => ({
        ...previous,
        [server.serverId]: {
          status: accessToken || isVaultBacked ? "verifying" : "resuming",
          errorMessage: null,
          serverUrl:
            previous[server.serverId]?.serverUrl ?? server.serverUrl ?? null,
        },
      }));
    },
    [
      isAuthenticated,
      isVaultBacked,
      pendingKey,
      scenarioId,
      surface,
      projectId,
    ]
  );

  useEffect(() => {
    authorizeServerRef.current = authorizeServer;
  }, [authorizeServer]);

  const markOAuthRequired = useCallback(
    (details?: HostedOAuthRequiredDetails) => {
      // Resolve targets OUTSIDE the updater. React runs updaters lazily during
      // the render pass, so ids collected inside one were not yet available to
      // the `setRuntimeRequiredServerIds` call below — the set stayed empty and
      // the guard that exists to survive a rebuild never fired. Clearing tokens
      // is a side effect and does not belong in an updater either.
      const matchingServers = oauthServers.filter((server) => {
        if (details?.serverId && server.serverId === details.serverId) {
          return true;
        }
        if (details?.serverName && server.serverName === details.serverName) {
          return true;
        }
        if (details?.serverUrl && server.serverUrl === details.serverUrl) {
          return true;
        }
        return false;
      });

      const fallbackServer =
        matchingServers.length > 0
          ? null
          : oauthServers.length === 1
          ? oauthServers[0]
          : null;
      const targetServers =
        matchingServers.length > 0
          ? matchingServers
          : fallbackServer
          ? [fallbackServer]
          : oauthServers;

      if (targetServers.length === 0) {
        // Nothing to escalate YET. A server joins this set only once the
        // requirement probe has answered for it, and that answer is fetched
        // asynchronously — so a real 401 can arrive while the set is still
        // empty. Dropping it there is what left the recipient of an `auto`
        // (discover) server with a failed tool call and no Authorize action,
        // since a runtime 401 is that server's ONLY route to a prompt. Hold the
        // request and replay it once servers arrive.
        setDeferredOAuthRequirement(details ?? null);
        return;
      }

      for (const server of targetServers) {
        runtimeRequiredEpochRef.current.set(
          server.serverId,
          (runtimeRequiredEpochRef.current.get(server.serverId) ?? 0) + 1
        );
        localStorage.removeItem(`mcp-tokens-${server.serverName}`);
      }

      setOAuthStateByServerId((previous) => {
        const nextState = { ...previous };
        for (const server of targetServers) {
          nextState[server.serverId] = {
            status: "needs_auth",
            errorMessage: details?.serverUrl ? null : RUNTIME_OAUTH_ERROR,
            serverUrl:
              details?.serverUrl ??
              previous[server.serverId]?.serverUrl ??
              server.serverUrl ??
              null,
          };
        }

        return nextState;
      });
      // Remember it outside the status map so a later rebuild keeps the prompt.
      setRuntimeRequiredServerIds((previous) => {
        if (targetServers.every((server) => previous.has(server.serverId))) {
          return previous;
        }
        return new Set([
          ...previous,
          ...targetServers.map((server) => server.serverId),
        ]);
      });
    },
    [oauthServers]
  );

  const markOAuthRequiredRef = useRef(markOAuthRequired);
  useEffect(() => {
    markOAuthRequiredRef.current = markOAuthRequired;
  }, [markOAuthRequired]);

  // Replay a 401 that beat the servers into the gate. Guarded on a non-empty
  // set so the replay always finds a target and cannot re-defer itself.
  useEffect(() => {
    if (deferredOAuthRequirement === undefined) return;
    if (oauthServers.length === 0) return;

    const details = deferredOAuthRequirement;
    setDeferredOAuthRequirement(undefined);
    markOAuthRequiredRef.current(details ?? undefined);
  }, [deferredOAuthRequirement, oauthServers]);

  const pendingOAuthServers = useMemo(
    () =>
      oauthServers
        .map((server) => ({
          server,
          state:
            oauthStateByServerId[server.serverId] ??
            ({
              // Same default as the state map: a server that does not require
              // authorization up front is satisfied, not pending.
              status:
                server.authorizationRequiredUpfront === false &&
                !runtimeRequiredServerIds.has(server.serverId)
                  ? "ready"
                  : "needs_auth",
              errorMessage: null,
              serverUrl: server.serverUrl,
            } satisfies HostedOAuthState),
        }))
        .filter(({ state }) => state.status !== "ready"),
    [oauthServers, oauthStateByServerId, runtimeRequiredServerIds]
  );

  const hasBusyOAuth = pendingOAuthServers.some(({ state }) =>
    isHostedOAuthBusy(state.status)
  );

  return {
    oauthStateByServerId,
    pendingOAuthServers,
    hasBusyOAuth,
    authorizeServer,
    markOAuthRequired,
  };
}
